import React, { useState, useEffect } from 'react';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { db } from '../firebase';
import { collection, getDocs, setDoc, deleteDoc, doc } from 'firebase/firestore';
import { supabase } from '../supabase';
import { sendGenericNotification, sendWelcomeAdmin } from '../services/emailService';
import { useAuth } from '../contexts/AuthContext';
import './AdminsPage.css';

// Secondary Firebase config — used to create new users without signing out the current admin
const secondaryFirebaseConfig = {
    apiKey: "AIzaSyBleAnPBGzpc4yH9fChB2Nmh-b1A-sxT8Y",
    authDomain: "skillhive-6600.firebaseapp.com",
    projectId: "skillhive-6600",
    storageBucket: "skillhive-6600.firebasestorage.app",
    messagingSenderId: "535174388656",
    appId: "1:535174388656:web:8ef433bad78f6792311475"
};

const AdminsPage = () => {
    const { currentUser } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({ name: '', email: '', password: '' });
    const [formError, setFormError] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            // Query users collection for admin role
            const snap = await getDocs(collection(db, 'users'));
            const adminList = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(u => u.role === 'admin' || u.userType === 'admin');
            setAdmins(adminList);
        } catch (err) {
            console.error('Error fetching admins:', err);
        }
        setLoading(false);
    };

    const handleAddAdmin = async (e) => {
        e.preventDefault();
        setFormError('');
        setSuccessMsg('');
        if (!formData.name || !formData.email || !formData.password) {
            setFormError('All fields are required.');
            return;
        }
        if (formData.password.length < 6) {
            setFormError('Password must be at least 6 characters.');
            return;
        }
        setFormLoading(true);
        let secondaryApp = null;
        try {
            // Use a secondary Firebase app so the current admin stays signed in
            secondaryApp = initializeApp(secondaryFirebaseConfig, 'adminCreation');
            const secondaryAuth = getAuth(secondaryApp);

            // Create Firebase Auth account on the secondary app
            const result = await createUserWithEmailAndPassword(secondaryAuth, formData.email, formData.password);
            const uid = result.user.uid;

            // Set display name on the new user
            await updateProfile(result.user, { displayName: formData.name });

            // Write admin record to Firestore 'users' collection (current admin is still signed in)
            await setDoc(doc(db, 'users', uid), {
                uid,
                name: formData.name,
                email: formData.email,
                userType: 'admin',
                role: 'admin',
                createdAt: new Date().toISOString(),
                createdBy: currentUser?.email || 'admin',
            });

            // Also write to 'admins' collection so isAdmin() Firestore rule check works
            await setDoc(doc(db, 'admins', uid), {
                uid,
                name: formData.name,
                email: formData.email,
                role: 'admin',
                createdAt: new Date().toISOString(),
                createdBy: currentUser?.email || 'admin',
            });

            // First-time admin creation: send branded onboarding email (best-effort).
            let emailStatus = 'Email sent.';
            try {
                await sendWelcomeAdmin(formData.email, {
                    adminName: formData.name,
                    name: formData.name,
                    email: formData.email,
                    createdBy: currentUser?.email || 'admin',
                    loginUrl: 'https://admin.skillhives.co.in/login',
                });
            } catch (emailError) {
                console.error('Admin welcome email failed:', emailError);
                try {
                    await sendGenericNotification(formData.email, {
                        title: 'Your SkillHive Admin Account Is Ready',
                        message: `Hi ${formData.name}, your admin account is ready. Login at https://admin.skillhives.co.in/login`,
                        portalType: 'admin',
                    });
                    emailStatus = 'Welcome template failed, fallback email sent.';
                } catch (fallbackError) {
                    console.error('Admin fallback email failed:', fallbackError);
                    emailStatus = 'Email failed. Check send-email function deploy/secrets/logs.';
                }
            }

            // Clean up secondary app
            await deleteApp(secondaryApp);
            secondaryApp = null;

            setSuccessMsg(`✅ Admin "${formData.name}" created successfully! ${emailStatus}`);
            setFormData({ name: '', email: '', password: '' });
            setShowForm(false);
            fetchAdmins();
        } catch (err) {
            if (err.code === 'auth/email-already-in-use') {
                // The Firebase Auth account still exists (delete failed previously).
                // Try to recover: look up existing UID, reset password, re-create Firestore docs.
                try {
                    // 1. Look up the existing UID for this email
                    const { data: lookupData, error: lookupErr } = await supabase.functions.invoke('firebase-admin', {
                        body: { action: 'getUserByEmail', email: formData.email },
                    });
                    if (lookupErr) throw new Error(lookupErr.message || JSON.stringify(lookupErr));
                    const existingUid = lookupData?.uid;
                    if (!existingUid) throw new Error('Could not retrieve existing UID.');

                    // 2. Reset their password and display name
                    const { error: updateErr } = await supabase.functions.invoke('firebase-admin', {
                        body: { action: 'updateUser', uid: existingUid, password: formData.password, displayName: formData.name },
                    });
                    if (updateErr) throw new Error(updateErr.message || JSON.stringify(updateErr));

                    // 3. Re-create the Firestore admin records with the existing UID
                    await setDoc(doc(db, 'users', existingUid), {
                        uid: existingUid,
                        name: formData.name,
                        email: formData.email,
                        userType: 'admin',
                        role: 'admin',
                        createdAt: new Date().toISOString(),
                        createdBy: currentUser?.email || 'admin',
                    });
                    await setDoc(doc(db, 'admins', existingUid), {
                        uid: existingUid,
                        name: formData.name,
                        email: formData.email,
                        role: 'admin',
                        createdAt: new Date().toISOString(),
                        createdBy: currentUser?.email || 'admin',
                    });

                    setSuccessMsg(`✅ Admin "${formData.name}" re-added successfully (existing auth account reused)!`);
                    setFormData({ name: '', email: '', password: '' });
                    setShowForm(false);
                    fetchAdmins();
                } catch (recoveryError) {
                    console.error('Auto-recovery failed:', recoveryError);
                    setFormError(
                        'This email already exists in Firebase Authentication and auto-recovery failed. ' +
                        'Go to Firebase Console → Authentication → Users, find this email, delete it manually, then try again. ' +
                        `(Reason: ${recoveryError.message})`
                    );
                }
            } else {
                setFormError(err.message);
            }
        } finally {
            if (secondaryApp) {
                try { await deleteApp(secondaryApp); } catch (e) { }
            }
        }
        setFormLoading(false);
    };

    const handleRevoke = async (admin) => {
        if (!window.confirm(`Permanently delete admin "${admin.name || admin.email}"?\n\nThis will remove their profile from Firestore AND delete their login account from Firebase Authentication.`)) return;
        try {
            // 1. Delete Firebase Auth account via the admin edge function
            try {
                await supabase.functions.invoke('firebase-admin', {
                    body: { action: 'deleteUser', uid: admin.id },
                });
            } catch (authErr) {
                console.warn('Firebase Auth user could not be deleted automatically:', authErr);
            }

            // 2. Delete from both Firestore collections
            await deleteDoc(doc(db, 'users', admin.id));
            try {
                await deleteDoc(doc(db, 'admins', admin.id));
            } catch (e) {
                // admins doc may not exist, that's fine
            }

            setAdmins(prev => prev.filter(a => a.id !== admin.id));
            setSuccessMsg(`✅ Admin "${admin.name || admin.email}" fully deleted (profile + login account).`);
        } catch (err) {
            alert('Error deleting admin: ' + err.message);
        }
    };

    return (
        <div className="admins-page">
            <div className="page-header">
                <div>
                    <h1>Admin Accounts</h1>
                    <p className="subtitle">Manage who has access to this admin panel</p>
                </div>
                <button className="btn-add" onClick={() => { setShowForm(!showForm); setFormError(''); setSuccessMsg(''); }}>
                    {showForm ? '✕ Cancel' : '+ Add Admin'}
                </button>
            </div>

            {successMsg && (
                <div className="alert alert-success">{successMsg}</div>
            )}

            {showForm && (
                <div className="form-card">
                    <h3>Create New Admin</h3>
                    <form onSubmit={handleAddAdmin}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Full Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Rohan Sharma"
                                    value={formData.name}
                                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                                />
                            </div>
                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    placeholder="admin@example.com"
                                    value={formData.email}
                                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                                />
                            </div>
                            <div className="form-group">
                                <label>Password *</label>
                                <input
                                    type="password"
                                    placeholder="Min 6 characters"
                                    value={formData.password}
                                    onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                                />
                            </div>
                        </div>
                        {formError && <div className="alert alert-error">{formError}</div>}
                        <button type="submit" className="btn-submit" disabled={formLoading}>
                            {formLoading ? 'Creating...' : 'Create Admin'}
                        </button>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="loading">Loading admins...</div>
            ) : admins.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🔐</div>
                    <h3>No admin records found in Firestore</h3>
                    <p>Your admin account (<strong>{currentUser?.email}</strong>) is authenticated via the bootstrap list and works fine.</p>
                    <p>Use <strong>"Add Admin"</strong> above to create additional admin accounts.</p>
                </div>
            ) : (
                <div className="admins-table-wrap">
                    <table className="admins-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {admins.map(admin => (
                                <tr key={admin.id}>
                                    <td>
                                        <div className="admin-avatar">{(admin.name || admin.email || 'A').charAt(0).toUpperCase()}</div>
                                        {admin.name || '—'}
                                    </td>
                                    <td>{admin.email}</td>
                                    <td><span className="badge">{admin.role || admin.userType}</span></td>
                                    <td>{admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : '—'}</td>
                                    <td>
                                        {admin.email !== currentUser?.email ? (
                                            <button className="btn-revoke" onClick={() => handleRevoke(admin)}>
                                                🗑️ Delete
                                            </button>
                                        ) : (
                                            <span className="you-badge">You</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminsPage;
